import { Box, ButtonBase, FormHelperText, InputLabel, Stack, Tooltip, alpha, styled } from '@mui/material'
import useId from '@mui/utils/useId'
import { useCallback } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { getLabOptionColorBackground } from '../../AppData/LabOptionColor'

export interface ColorFieldOption<Value extends string | number = string | number> {
  value: Value
  label: ReactNode
  color: string
  disabled?: boolean
}

interface Props<Value extends string | number = string | number> {
  id?: string
  label?: ReactNode
  value?: Value | ''
  options: Array<ColorFieldOption<Value>>
  onChange: (value: Value) => void
  disabled?: boolean
  error?: boolean
  helperText?: ReactNode
}

const PatchButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  width: 34,
  height: 34,
  borderRadius: 7,
  padding: 3,
  border: '1px solid rgba(0, 0, 0, 0.16)',
  backgroundColor: '#fff',
  boxShadow: selected ? '0 0 0 2px ' + theme.palette.secondary.main : 'none',
  transition: 'box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease',

  '&:hover': {
    //transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.secondary.main, 0.55),
    boxShadow: selected
      ? '0 0 0 2px ' + theme.palette.secondary.main
      : 'none',
  },

  '&.Mui-disabled': {
    opacity: 0.35,
  },
}))

const PatchSwatch = styled('span')({
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: 5,
  boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
})

export default function ColorField<Value extends string | number = string | number>(props: Props<Value>) {
  const [t] = useTranslation('common');

  const id = useId(props.id)
  const hasLabel = Boolean(props.label)
  const hasHelperText = Boolean(props.helperText)
  const labelId = hasLabel ? id + '-label' : undefined

  const selectedOption = props.options.find(option => String(option.value) === String(props.value))

  const renderOption = useCallback((option: ColorFieldOption<Value>) => {
    const selected = String(option.value) === String(props.value)
    const optionLabel = typeof option.label === 'string' ? option.label : undefined

    return <Tooltip
      key={String(option.value)}
      title={option.label}
    >
      <span>
        <PatchButton
          type="button"
          role="radio"
          selected={selected}
          aria-checked={selected}
          aria-label={optionLabel}
          disabled={props.disabled === true || option.disabled === true}
          onClick={() => props.onChange(option.value)}
        >
          <PatchSwatch style={{ background: getLabOptionColorBackground(option.color) }} />
        </PatchButton>
      </span>
    </Tooltip>
  }, [props.disabled, props.onChange, props.value])

  return <Box component="div">
    {hasLabel ? <InputLabel
      id={labelId}
      htmlFor={id}
      error={props.error}
      disabled={props.disabled}
    >{selectedOption !== undefined ? t('{{label}}: {{value}}', { label: props.label, value: selectedOption.label }) : props.label}</InputLabel> : null}
    <Stack
      id={id}
      role="radiogroup"
      aria-labelledby={labelId}
      direction="row"
      flexWrap="wrap"
      useFlexGap
      gap="8px"
    >
      {props.options.map(renderOption)}
    </Stack>
    {hasHelperText ? <FormHelperText error={props.error}>{props.helperText}</FormHelperText> : null}
  </Box>
}
